import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Group,
  Indicator,
  Loader,
  NavLink,
  Stack,
  Text,
} from "@mantine/core";
import { IconLifebuoy, IconPlus } from "@tabler/icons-react";
import { SupportTicket } from "@/app/@types/support";
import useFormatDate from "@/app/_hooks/useFormatDate";
import { getSupportCategory } from "@/app/_constants/support";
import SupportStatusBadge from "./SupportStatusBadge";
import SupportFooter from "./SupportFooter";

interface SupportTicketListProps {
  tickets: SupportTicket[];
  isLoading: boolean;
  onSelect: (ticket: SupportTicket) => void;
  onNew: () => void;
}

const TicketRow = ({
  ticket,
  onSelect,
}: {
  ticket: SupportTicket;
  onSelect: (ticket: SupportTicket) => void;
}) => {
  const formatDate = useFormatDate();
  const category = getSupportCategory(ticket.category);
  // Date resolution only: recency without a stopwatch.
  const updated = ticket.updatedAt?.toDate ? formatDate(ticket.updatedAt) : "";

  return (
    <NavLink
      component="button"
      onClick={() => onSelect(ticket)}
      aria-label={`Open ticket #${ticket.ticketNumber}`}
      px="md"
      py="sm"
      w="100%"
      styles={{
        root: { maxWidth: "100%" },
        body: { minWidth: 0 },
        label: { minWidth: 0 },
        section: { flexShrink: 0 },
      }}
      leftSection={
        <Indicator
          color="primary"
          size={8}
          disabled={!ticket.unreadForUser}
          offset={2}
        >
          <Box w={6} h={6} />
        </Indicator>
      }
      label={
        <Group gap="xs" wrap="nowrap" miw={0}>
          <Text size="sm" c="dimmed" fw={600} style={{ flexShrink: 0 }}>
            #{ticket.ticketNumber}
          </Text>
          <Text
            size="sm"
            fw={ticket.unreadForUser ? 700 : 500}
            truncate="end"
            flex={1}
            miw={0}
          >
            {ticket.subject}
          </Text>
        </Group>
      }
      description={
        <Text size="xs" c="dimmed" truncate="end" mt={2}>
          {category.label} · Updated {updated}
        </Text>
      }
      rightSection={<SupportStatusBadge status={ticket.status} size="sm" />}
      style={{
        borderBottom: "1px solid var(--mantine-color-default-border)",
      }}
    />
  );
};

const SupportTicketList = ({
  tickets,
  isLoading,
  onSelect,
  onNew,
}: SupportTicketListProps) => {
  const [showResolved, setShowResolved] = useState(false);

  const { active, resolved } = useMemo(() => {
    return {
      active: tickets.filter((t) => t.status !== "resolved"),
      resolved: tickets.filter((t) => t.status === "resolved"),
    };
  }, [tickets]);

  return (
    <>
      <Box flex={1} mih={0}>
        <Box h="100%" style={{ overflowY: "auto", overflowX: "hidden" }}>
          {isLoading ? (
            <Group justify="center" py="xl">
              <Loader size="sm" />
            </Group>
          ) : tickets.length === 0 ? (
            <Stack align="center" gap="sm" py={48} px="md">
              <IconLifebuoy size={40} stroke={1.2} />
              <Text fw={600}>No tickets yet</Text>
              <Text size="sm" c="dimmed" ta="center" maw={320}>
                Report an issue, suggest an improvement, or ask a question.
                Your tickets and our replies will show up here.
              </Text>
            </Stack>
          ) : (
            <Stack gap={0}>
              {active.length === 0 && (
                <Text size="sm" c="dimmed" ta="center" py="lg" px="md">
                  No open tickets.
                </Text>
              )}
              {active.map((ticket) => (
                <TicketRow
                  key={ticket.uid}
                  ticket={ticket}
                  onSelect={onSelect}
                />
              ))}
              {resolved.length > 0 && (
                <>
                  <Divider />
                  <NavLink
                    component="button"
                    px="md"
                    py="xs"
                    label={
                      <Text size="sm" fw={600} c="dimmed">
                        Resolved ({resolved.length})
                      </Text>
                    }
                    opened={showResolved}
                    onChange={setShowResolved}
                    childrenOffset={0}
                  >
                    {resolved.map((ticket) => (
                      <TicketRow
                        key={ticket.uid}
                        ticket={ticket}
                        onSelect={onSelect}
                      />
                    ))}
                  </NavLink>
                </>
              )}
            </Stack>
          )}
        </Box>
      </Box>
      <SupportFooter>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={onNew}
          size="md"
          fullWidth
        >
          New ticket
        </Button>
      </SupportFooter>
    </>
  );
};

export default SupportTicketList;
